<template>
  <div class="event" v-if="!page404">
    <XNav v-if="event" :content="nav" />
    <h1 v-if="event && event.HomeTeam_Name" class="sr-only">{{ event.HomeTeam_Name }} vs {{ event.AwayTeam_Name }}</h1>
    <Banner
      v-if="event"
      :title="
        $DateTime
          .fromISO(event.Date)
          .setLocale($i18n.locale)
          .toFormat('MMM d')
      "
      :subtitle="event.SeasonName + ' - ' + event.LeagueName"
    />
    <div class="info" v-if="event">
      <div class="team-name">
        <img :src="`${$bucketUrl}/images/teams/${event.HomeTeam_id}.png`" />
        <p>
          <router-link :to="`/team/${event.HomeTeam_id}/${event.League_id}`">{{
            event.HomeTeam_Name
          }}</router-link>
        </p>
      </div>
      <div class="data">
        <p class="score">{{ event.Home_Score }} - {{ event.Away_Score }}</p>
        <p>
          <a :href="'http://maps.google.com/?q=' + event.Address">{{
            event.Field
          }}</a>
          <br />
          {{
            $DateTime
              .fromISO(event.Date)
              .setLocale($i18n.locale)
              .toFormat("h:mm a")
          }}
        </p>
      </div>
      <div class="team-name">
        <img :src="`${$bucketUrl}/images/teams/${event.AwayTeam_id}.png`" />
        <p>
          <router-link :to="`/team/${event.AwayTeam_id}/${event.League_id}`">{{
            event.AwayTeam_Name
          }}</router-link>
        </p>
      </div>
    </div>
    <button class="share-button" v-if="sharable" @click="share">
      <span class="icon">&#xe804;</span>{{ $t("common.shareThisPage") }}
    </button>
    <div class="tables" v-if="event.homeTeamResults && event.awayTeamResults">
      <EventTable :x-title="event.HomeTeam_Name" :content="event.homeTeamResults" />
      <EventTable :x-title="event.AwayTeam_Name" :content="event.awayTeamResults" />
    </div>
  </div>
  <Page404 v-else />
</template>

<script>
import axios from "axios";
import EventTable from "@/components/tables/EventTable.vue";
import XNav from "@/components/XNav.vue";
import Banner from "@/components/Banner.vue";
import Page404 from "@/views/404.vue";
export default {
  components: {
    EventTable,
    XNav,
    Banner,
    Page404,
  },
  metaInfo() {
    return {
      title: this.$t("seo_metadata.match.title", this.seoData),
      meta: [
        { vmid: "description", name: "description", content: this.$t("seo_metadata.match.description", this.seoData) },
        { vmid: "keywords", name: "keywords", content: this.$t("seo_metadata.keywords") },
        { vmid: "og:title", property: "og:title", content: this.$t("seo_metadata.match.title", this.seoData) },
        { vmid: "og:description", property: "og:description", content: this.$t("seo_metadata.match.description", this.seoData) },
        { vmid: "og:url", property: "og:url", content: `https://kingsleague.ca/event/${this.$route.params.id}` },
        { vmid: "og:type", property: "og:type", content: "website"},
        { vmid: "twitter:title", name: "twitter:title", content: this.$t("seo_metadata.match.title", this.seoData) },
        { vmid: "twitter:description", name: "twitter:description", content: this.$t("seo_metadata.match.description", this.seoData) }
      ],
      link: [{ rel: "canonical", href: "https://kingsleague.ca/event/" + this.$route.params.id }],
      script: this.event && this.event.HomeTeam_Name ? [{
        type: "application/ld+json",
        json: {
          "@context": "https://schema.org",
          "@type": "SportsEvent",
          "name": this.event.HomeTeam_Name + " vs " + this.event.AwayTeam_Name,
          "startDate": this.event.Date,
          "location": {
            "@type": "Place",
            "name": this.event.Field,
            "address": this.event.Address
          },
          "homeTeam": {
            "@type": "SportsTeam",
            "name": this.event.HomeTeam_Name
          },
          "awayTeam": {
            "@type": "SportsTeam",
            "name": this.event.AwayTeam_Name
          },
          "organizer": {
            "@type": "SportsOrganization",
            "name": "The Kings League",
            "url": "https://kingsleague.ca"
          },
          "url": "https://kingsleague.ca/event/" + this.$route.params.id
        }
      }, {
        type: "application/ld+json",
        json: {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://kingsleague.ca/" },
            { "@type": "ListItem", "position": 2, "name": this.event.LeagueName || "Leagues", "item": "https://kingsleague.ca/" },
            { "@type": "ListItem", "position": 3, "name": this.event.HomeTeam_Name + " vs " + this.event.AwayTeam_Name }
          ]
        }
      }] : []
    };
  },
  data() {
    return {
      event: {},
      sharable: !!navigator.share,
      page404: false,
    };
  },
  mounted() {
    axios
      .get(`${this.$apiUrl}/events/${this.$route.params.id}`)
      .then((res) => {
        this.event = res.data[0];
        console.log(this.event);
      })
      .then(() => this.$store.dispatch("pageLoad", false))
      .catch(() => {
        this.page404 = true;
        this.$store.dispatch("pageLoad", false);
      });
  },
  methods: {
    share() {
      navigator.share({
        title: this.event.HomeTeam_Name + " vs " + this.event.AwayTeam_Name,
        url: window.location.href,
      });
    },
  },
  computed: {
    seoData() {
      return { 
        home: this.event.HomeTeam_Name,
        away: this.event.AwayTeam_Name,
        season: this.event.SeasonName,
        league: this.event.LeagueName
      };
    },
    nav() {
      if (!this.event) return [{ name: "Events" }];

      return [
        { t: "common.leagues" },
        { name: this.event.LeagueName },
        {
          name:
            this.$DateTime
              .fromISO(this.event.Date)
              .setLocale(this.$i18n.locale)
              .toFormat("MMM d") +
            " - " +
            this.event.HomeTeam_Name +
            " vs " +
            this.event.AwayTeam_Name,
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/theme.scss";
.event {
  padding-top: 15px;
}
.info {
  @extend %light-pattern;
  margin-left: 0;
  margin-right: 0;
  display: flex;
  position: relative;
}
.team-name {
  font-size: 24px;
  flex: 1 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  img {
    height: 170px;
    margin: 15px;
    //object-fit: cover;
    //object-position: center center;
  }
  p {
    margin: 15px;
    text-align: center;
    a {
      color: black;
      text-decoration: none;
      display: block;
      &:hover {
        text-decoration: underline;
      }
    }
  }
}
.data {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  text-align: center;
  padding-top: 15px;
  a {
    color: black;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
}
.score {
  @extend %font-bold;
  font-size: 60px;
}
h2 {
  background-color: $primary;
  font-size: 1.7em;
  padding: 0.1em 1em;
  color: white;
  font-weight: 200;
  margin-top: 15px;
}
th {
  text-align: right;
}
.logo {
  margin-bottom: -1.2em;
  height: 3em;
  width: 3em;
  display: inline-block;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
  margin-left: 15px;
  margin-right: 15px;
}
.tables {
  display: flex;
  margin: 0 -15px;
  align-items: stretch;
  & > div {
    flex: 1 1;
    margin: 30px 15px;
  }
}
.share-button {
  margin: 15px 0;
  font-size: 20px;
  height: 50px;
  width: 100%;
  padding: 10px;
  background-color: $secondary;
  border: none;
  color: white;
}
@media only screen and (max-width: $mobile-breakpoint) {
  .tables {
    flex-direction: column;
  }
  .score {
    font-size: 50px;
  }
  .team-name {
    img {
      height: 100px;
    }
  }
}
</style>
